import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listNextBestActions, mapApiNextBestActionToUi } from '../../../services/nextBestAction.js';

/**
 * The Next Best Action suggestions of one record (CT-5713). Called ONCE by the
 * profile page, so switching tabs never re-requests them.
 *
 * `refresh` is quiet: after a workflow starts, the card keeps its content until
 * the shorter list arrives instead of collapsing into a skeleton.
 */
export function useNextBestActions({ entityType, entityId } = {}) {
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [restricted, setRestricted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  // The record open NOW: a `refresh` captured for the previous one (a workflow
  // started there that answers late) must neither show that record's
  // suggestions nor cancel this one's request.
  const recordKey = `${entityType}/${entityId}`;
  const openRecordRef = useRef(recordKey);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!entityType || !entityId) return;
      if (`${entityType}/${entityId}` !== openRecordRef.current) return;
      const requestId = ++fetchIdRef.current;
      // A full load starts empty: another record never shows — nor lets anyone
      // act on — the previous record's suggestions while its own arrive.
      if (!quiet) {
        setLoading(true);
        setError(null);
        setItems([]);
      }

      try {
        const { body } = await listNextBestActions({ entityType, entityId });
        if (requestId !== fetchIdRef.current) return;
        setItems((body?.items ?? []).map(mapApiNextBestActionToUi));
        setUpdatedAt(body?.updated_at ?? body?.updatedAt ?? null);
        setCheckedAt(body?.checked_at ?? body?.checkedAt ?? null);
        setRestricted(Boolean(body?.restricted));
        setError(null);
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setItems([]);
        setError(err?.message ?? 'Failed to load the suggestions');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);

  // Declared BEFORE the fetch effect, so the new record is "open" when it loads.
  useEffect(() => {
    openRecordRef.current = recordKey;
  }, [recordKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ items, updatedAt, checkedAt, restricted, loading, error, refetch, refresh }),
    [items, updatedAt, checkedAt, restricted, loading, error, refetch, refresh],
  );
}

export default useNextBestActions;
